// PlugitAI — brand configuration source of truth.
// Change any value here and it propagates across every page/component
// that imports from this file. Never hardcode brand strings in components.

export const BRAND = {
  // Public / customer-facing
  platformName: "PlugitAI",
  platformTagline: "The AI operating system for every business you run.",
  platformDomain: "plugitai.com",
  platformUrl: "https://plugitai.com",

  // Internal engine (only surfaced in the footer credit)
  engineName: "Nariai",
  engineFullName: "Nariai Core",
  engineTagline: "Intelligent Systems. Real Results.",

  // AI / Bot division
  botName: "BotProviderAI",
  botTagline: "Sales bots that close, support bots that solve, schedules that stick.",

  // Compound
  footerCredit: "PlugitAI · Powered by Nariai · BotProviderAI inside",
  legalFooter: "© 2026 PlugitAI. Built on Nariai. All rights reserved.",

  // Feb 2026 revamp — "Neon Studio" palette. Kept legacy color KEYS so the
  // 23 files that reference BRAND.colors.blueBright / greenGlow / etc keep
  // rendering. VALUES swapped from stock blue+green → electric violet +
  // canary amber for a premium, creative, unmistakably-not-a-template feel.
  // The canary amber ties directly into the "P" favicon + SEO-page accents,
  // creating cross-site cohesion; the electric violet swaps for the blue.
  colors: {
    // "blue*" keys → electric violet family (legacy naming, new hue)
    blueDeep: "#4C1D95",      // deep violet, was #0B2A6B
    blueBright: "#8B5CF6",    // primary violet accent, was #1E5FE0
    blueGlow: "#A78BFA",      // soft violet glow, was #3B82F6
    // "green*" keys → canary amber family (legacy naming, new hue)
    greenBright: "#FCD34D",   // primary canary amber, was #22C55E
    greenGlow: "#FDE68A",     // soft cream-gold glow, was #4ADE80
    // Canvas / typography (unchanged — keeps dark base for drama)
    ink: "#0A0F1E",           // near-black canvas
    inkSoft: "#111827",       // card canvas
    line: "rgba(255,255,255,0.08)",
    text: "#F5F7FB",
    textDim: "rgba(245,247,251,0.7)",
    textFade: "rgba(245,247,251,0.5)",
    // Named aliases for new components — prefer these going forward
    violet: "#8B5CF6",
    violetSoft: "#A78BFA",
    amber: "#FCD34D",
    amberSoft: "#FDE68A",
  },

  // Logos on disk (public/)
  logos: {
    plugitai: "/plugitai-logo.svg",           // full wordmark, WHITE letters + blue plug (for dark surfaces)
    plugitaiDark: "/plugitai-logo-dark.svg",  // full wordmark, NAVY letters + blue plug (for light surfaces)
    plugitaiMark: "/plugitai-mark.svg",       // plug icon only (square)
    nariai2026: "/nariai-logo-2026.png",
    nariaiClassic: "/nariai-logo.png",
    plugitaiFavicon: "/plugitai-favicon.ico",
    plugitaiGold256: "/plugitai-logo-256.png",   // 18K gold wordmark · 256px
    plugitaiGold512: "/plugitai-logo-512.png",   // 18K gold wordmark · 512px
    plugitaiGold1024: "/plugitai-logo-1024.png", // 18K gold wordmark · 1024px
  },
};

// Live client sites featured on the PlugitAI marketing page.
// Add new entries here as clients ship. `screenshot` is a path in /public/showcase/.
export const SHOWCASE = [
  {
    slug: "texasallworld",
    name: "Texas All World Casino & Games",
    industry: "Luxury casino & resort · concept build",
    location: "Lake Lewisville, Texas · Coming 2030",
    url: null,
    noLink: true,
    conceptOnly: true,
    screenshotDesktop: "/showcase/texas-all-world.png",
    screenshotMobile: "/showcase/texas-all-world.png",
    accent: "#D4AF37",
    metrics: [
      { label: "Launch", value: "2030" },
      { label: "Location", value: "Lewisville" },
      { label: "Stage", value: "Vision site" },
    ],
  },
  {
    slug: "jsworldtile",
    name: "JS World of Tile",
    industry: "Premium tile · design + installation",
    location: "DFW · concept build",
    url: null,
    noLink: true,
    conceptOnly: true,
    screenshotDesktop: "/showcase/js-world-tile.png",
    screenshotMobile: "/showcase/js-world-tile.png",
    accent: "#2563EB",
    metrics: [
      { label: "Years exp", value: "20+" },
      { label: "Projects", value: "5,000+" },
      { label: "Serving", value: "DFW" },
    ],
  },
  {
    slug: "richardpetty",
    name: "Richard Petty Motorsports",
    industry: "NASCAR legacy · concept refresh",
    location: "The King · concept build",
    url: null,
    noLink: true,
    conceptOnly: true,
    screenshotDesktop: "/showcase/richard-petty.png",
    screenshotMobile: "/showcase/richard-petty.png",
    accent: "#1E40AF",
    metrics: [
      { label: "Championships", value: "7" },
      { label: "Wins", value: "200" },
      { label: "Class of", value: "Inaugural HoF" },
    ],
  },
  {
    slug: "lonestarlumpers",
    name: "Lone Star Lumpers",
    industry: "DFW warehouse labor & freight support · 24/7 dispatch",
    location: "Dallas · Fort Worth · Arlington",
    url: "https://lonestarlumper.com",
    screenshotDesktop: "/showcase/lonestar-lumpers-desktop.jpg",
    screenshotMobile: "/showcase/lonestar-lumpers-mobile.jpg",
    accent: "#DC2626", // Lone Star Lumpers red
    justLaunched: true,
    launchedAt: "2026-08-03T17:15:00-05:00", // Aug 3, 5:15 PM CT
    metrics: [
      { label: "Response time", value: "60 min" },
      { label: "Dispatch", value: "24/7" },
      { label: "BBB", value: "A+" },
    ],
  },
  {
    slug: "missrodriguez",
    name: "Miss Rodriguez Pest Control",
    industry: "Residential + commercial pest control",
    location: "DFW · Dallas, Plano, Frisco, McKinney, Arlington",
    url: null,
    screenshotDesktop: "/showcase/miss-rodriguez-home.png",
    screenshotMobile: "/showcase/miss-rodriguez-home.png",
    accent: "#8DC63F", // pest-control green
    metrics: [
      { label: "Build cost", value: "< $2,000" },
      { label: "Leads/day now", value: "~16" },
      { label: "Close rate", value: "~50%" },
    ],
  },
  {
    slug: "cargopros",
    name: "CargoPros",
    industry: "Moving + cargo · licensed & insured",
    location: "Central Valley, California",
    url: "https://cargo-pros.com",
    screenshotDesktop: "/showcase/cargo-pros-desktop.jpg",
    screenshotMobile: "/showcase/cargo-pros-mobile.jpg",
    accent: "#EF4444", // CargoPros brand red
    metrics: [
      { label: "Instant quote", value: "Yes" },
      { label: "SMS lead capture", value: "Live" },
      { label: "Coverage", value: "Central Valley" },
    ],
  },
  {
    slug: "lonestar",
    name: "Lone Star Striping Pros",
    industry: "Commercial parking-lot striping",
    location: "DFW, Texas",
    url: "https://lonestarstriping.pro",
    screenshotDesktop: "/showcase/lonestar-live-desktop.jpg",
    screenshotMobile: "/showcase/lonestar-mobile.jpg",
    accent: "#D4AF37", // gold
    metrics: [
      { label: "Live pages", value: "266+" },
      { label: "Languages", value: "EN · ES" },
      { label: "AI chat", value: "24/7" },
    ],
  },
  {
    slug: "leenrx",
    name: "LeenRx",
    industry: "GLP-1 telehealth · weight loss",
    location: "USA · nationwide",
    url: "https://leenrx.online",
    screenshotDesktop: "/showcase/leenrx-desktop.jpg",
    screenshotMobile: "/showcase/leenrx-desktop.jpg",
    accent: "#22C55E", // green (medical / clinical)
    metrics: [
      { label: "Vertical", value: "Telehealth" },
      { label: "Bots", value: "Stealth + Chat" },
      { label: "Coverage", value: "50 states" },
    ],
  },
  {
    slug: "bestrx",
    name: "BestRx",
    industry: "Compounding pharmacy · weight loss",
    location: "USA · direct-to-patient",
    url: "https://bestrx.online",
    screenshotDesktop: "/showcase/bestrx-desktop.jpg",
    screenshotMobile: "/showcase/bestrx-desktop.jpg",
    accent: "#EF4444", // red (matches their brand)
    metrics: [
      { label: "Vertical", value: "Pharmacy" },
      { label: "Languages", value: "EN · ES" },
      { label: "Starting", value: "$39 / wk" },
    ],
  },
];
